import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Search,
  Video,
  Filter,
  MoreVertical,
  Trash2,
  Tag,
  Upload,
  Play,
  Clock,
  Calendar,
  Youtube,
  Link,
  Copy,
  ExternalLink
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const MediaLibraryPage = () => {
  const { api, token } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await api.get('/media');
      setMedia(response.data);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const deleteMedia = async (id) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    
    try {
      await api.delete(`/media/${id}`);
      setMedia(media.filter(m => m.id !== id));
      toast.success('Vidéo supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const uploadToYouTube = async () => {
    if (!selectedMedia) return;
    
    setUploading(true);
    try {
      const response = await api.post('/youtube/upload', {
        media_id: selectedMedia.id,
        title: selectedMedia.original_filename,
        description: 'Formation Valnexo Academy',
        privacy: 'unlisted'
      });
      
      setMedia(media.map(m =>
        m.id === selectedMedia.id
          ? { ...m, youtube_url: response.data.youtube_url, youtube_status: 'uploaded' }
          : m
      ));
      
      toast.success('Vidéo publiée sur YouTube !');
      setShowYouTubeDialog(false);
    } catch (error) {
      toast.error('Erreur lors de la publication');
    } finally {
      setUploading(false);
    }
  };

  const copyYouTubeLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Lien copié !');
  };

  const filteredMedia = media.filter(m => {
    const matchesSearch = m.original_filename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' 
      || (statusFilter === 'youtube' && m.youtube_status === 'uploaded')
      || (statusFilter === 'local' && m.youtube_status !== 'uploaded');
    return matchesSearch && matchesStatus;
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVideoUrl = (mediaItem) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    return `${baseUrl}/api/media/${mediaItem.id}/download?auth=${token}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold text-slate-900 font-['Outfit']">
            Bibliothèque média
          </h1>
          <p className="text-slate-500 mt-1">
            {media.length} vidéos • {media.filter(m => m.youtube_status === 'uploaded').length} sur YouTube
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="search-media-input"
            placeholder="Rechercher une vidéo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="media-status-filter">
            <Filter className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Filtrer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les vidéos</SelectItem>
            <SelectItem value="youtube">Sur YouTube</SelectItem>
            <SelectItem value="local">Non publiées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Media Grid */}
      {filteredMedia.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Video className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {searchQuery ? 'Aucun résultat' : 'Aucune vidéo'}
            </h3>
            <p className="text-slate-500">
              {searchQuery 
                ? 'Essayez avec d\'autres termes de recherche'
                : 'Enregistrez votre première vidéo depuis le studio'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredMedia.map((item) => (
            <Card 
              key={item.id} 
              className="border border-slate-200 hover:shadow-md transition-all group"
              data-testid={`media-card-${item.id}`}
            >
              <CardContent className="p-0">
                {/* Thumbnail */}
                <div 
                  className="aspect-video bg-slate-900 relative overflow-hidden cursor-pointer"
                  onClick={() => {
                    setSelectedMedia(item);
                    setShowPreview(true);
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-white fill-white" />
                    </div>
                  </div>
                  
                  {/* YouTube badge */}
                  {item.youtube_status === 'uploaded' && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs font-medium rounded flex items-center gap-1">
                      <Youtube className="w-3 h-3" />
                      YouTube
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-slate-900 line-clamp-1 text-sm">
                      {item.original_filename}
                    </h3>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-slate-100 transition-colors">
                          <MoreVertical className="w-4 h-4 text-slate-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => {
                            setSelectedMedia(item);
                            setShowPreview(true);
                          }}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Lire
                        </DropdownMenuItem>
                        {item.youtube_status !== 'uploaded' ? (
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedMedia(item);
                              setShowYouTubeDialog(true);
                            }}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Publier sur YouTube
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => copyYouTubeLink(item.youtube_url)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copier le lien YouTube
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => deleteMedia(item.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(item.created_at).split(',')[0]}
                    </span>
                    <span>{formatFileSize(item.size)}</span>
                  </div>

                  {item.youtube_url && (
                    <a
                      href={item.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Voir sur YouTube
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedMedia?.original_filename}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {selectedMedia && (
              <video
                src={getVideoUrl(selectedMedia)}
                controls
                autoPlay
                className="w-full h-full"
              />
            )}
          </div>
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>Taille: {selectedMedia && formatFileSize(selectedMedia.size)}</span>
            <span>Créée le: {selectedMedia && formatDate(selectedMedia.created_at)}</span>
          </div>
        </DialogContent>
      </Dialog>

      {/* YouTube Upload Dialog */}
      <Dialog open={showYouTubeDialog} onOpenChange={setShowYouTubeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publier sur YouTube</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              La vidéo <strong>{selectedMedia?.original_filename}</strong> sera publiée en mode <strong>non répertorié</strong>.
            </p>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>Note MVP:</strong> Cette fonctionnalité est simulée. En production, la vidéo sera réellement uploadée sur YouTube via l'API.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowYouTubeDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={uploadToYouTube}
              disabled={uploading}
              className="bg-red-600 hover:bg-red-700"
            >
              {uploading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <Youtube className="w-4 h-4 mr-2" />
              )}
              Publier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaLibraryPage;
